import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SlurryTestOutputSummaryComponent } from '../../popups/slurry-test-output-summary/slurry-test-output-summary.component';
import {
  SlurryCapabilitiesDto,
  SlurryCapabilityItemDto
} from '../../../../services/slurry.service';
import {
  OtherTestKind,
  OtherTestRow,
  OtherTestSectionConfig,
  SlurryOutputActionContext,
  TestNoteRow
} from '../../slurry-details.models';
import { AppendNoteDialogComponent } from '../../../append-note-dialog/append-note-dialog.component';

interface OtherTestAttachmentsRequest {
  testInputMasterId: number;
  title: string;
}

@Component({
  selector: 'app-slurry-other-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, SlurryTestOutputSummaryComponent],
  templateUrl: './slurry-other-tab.component.html',
  styleUrls: ['./slurry-other-tab.component.css']
})
export class SlurryOtherTabComponent implements OnChanges {
  @Input() otherAcidSolubilityRows: OtherTestRow[] = [];
  @Input() otherApiSedimentationRows: OtherTestRow[] = [];
  @Input() otherExpansionRows: OtherTestRow[] = [];
  @Input() otherPressurizedMudScaleRows: OtherTestRow[] = [];
  @Input() otherPycnometerRows: OtherTestRow[] = [];
  @Input() shearBondRows: OtherTestRow[] = [];
  @Input() otherSplittingTsRows: OtherTestRow[] = [];
  @Input() otherTriaxilConfinedCsRows: OtherTestRow[] = [];
  @Input() otherUnconfinedCsRows: OtherTestRow[] = [];
  @Input() otherBasicCrushRows: OtherTestRow[] = [];

  @Input() capabilities: SlurryCapabilitiesDto | null = null;
  @Input() isParentProjectCompleted = false;
  @Input() statusId = 1;
  @Input() slurryId = 0;
  @Input() testsMessage = '';
  @Input() parentBlockedTestsActionMessage = '';
  @Input() testAttachmentCounts: Record<number, number> = {};

  @Output() outputActionRequested = new EventEmitter<SlurryOutputActionContext>();
  @Output() singleTestRemoveRequested = new EventEmitter<number>();
  @Output() testAttachmentsRequested = new EventEmitter<OtherTestAttachmentsRequest>();

  blockedTestsActionMessage = '';

  readonly otherTestSections: OtherTestSectionConfig[] = [
    { kind: 'acidSolubility', title: 'Acid Solubility', addLabel: 'Add test' },
    { kind: 'apiSedimentation', title: 'API Sedimentation', addLabel: 'Add test' },
    { kind: 'expansion', title: 'Expansion', addLabel: 'Add test' },
    { kind: 'pressurizedMudScale', title: 'Pressurized Mud Scale', addLabel: 'Add test' },
    { kind: 'pycnometer', title: 'Pycnometer', addLabel: 'Add test' },
    { kind: 'shearBond', title: 'Shear Bond', addLabel: 'Add test', supportsCureTime: true },
    { kind: 'splittingTs', title: 'Splitting Tensile Strength', addLabel: 'Add test' },
    { kind: 'triaxialConfinedCs', title: 'Triaxial Confined Compressive Strength', addLabel: 'Add test' },
    { kind: 'unconfinedCs', title: 'Unconfined Compressive Strength', addLabel: 'Add test' },
    { kind: 'basicCrush', title: 'Basic Crush', addLabel: 'Add test' }
  ];

  private readonly sectionSelected: Record<OtherTestKind, boolean> = {
    acidSolubility: false,
    apiSedimentation: false,
    expansion: false,
    pressurizedMudScale: false,
    pycnometer: false,
    shearBond: false,
    splittingTs: false,
    triaxialConfinedCs: false,
    unconfinedCs: false,
    basicCrush: false
  };

  constructor(private readonly dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['otherAcidSolubilityRows'] ||
      changes['otherApiSedimentationRows'] ||
      changes['otherExpansionRows'] ||
      changes['otherPressurizedMudScaleRows'] ||
      changes['otherPycnometerRows'] ||
      changes['shearBondRows'] ||
      changes['otherSplittingTsRows'] ||
      changes['otherTriaxilConfinedCsRows'] ||
      changes['otherUnconfinedCsRows'] ||
      changes['otherBasicCrushRows']
    ) {
      this.syncSectionSelectionFromRows();
    }
  }

  getOtherTestRows(kind: OtherTestKind): OtherTestRow[] {
    switch (kind) {
      case 'acidSolubility': return this.otherAcidSolubilityRows;
      case 'apiSedimentation': return this.otherApiSedimentationRows;
      case 'expansion': return this.otherExpansionRows;
      case 'pressurizedMudScale': return this.otherPressurizedMudScaleRows;
      case 'pycnometer': return this.otherPycnometerRows;
      case 'shearBond': return this.shearBondRows;
      case 'splittingTs': return this.otherSplittingTsRows;
      case 'triaxialConfinedCs': return this.otherTriaxilConfinedCsRows;
      case 'unconfinedCs': return this.otherUnconfinedCsRows;
      case 'basicCrush': return this.otherBasicCrushRows;
    }
  }

  isSectionVisible(kind: OtherTestKind): boolean {
    return this.canSaveTests() || this.isSectionSelected(kind);
  }

  isSectionSelected(kind: OtherTestKind): boolean {
    return this.sectionSelected[kind];
  }

  onSectionSelectionChanged(kind: OtherTestKind, selected: boolean): void {
    this.blockedTestsActionMessage = '';
    this.sectionSelected[kind] = selected;
    if (!selected) {
      this.clearSectionRows(kind);
    }
  }

  addOtherTestRow(kind: OtherTestKind): void {
    this.blockedTestsActionMessage = '';
    this.sectionSelected[kind] = true;

    const row: OtherTestRow = kind === 'shearBond'
      ? { cureTimeHrs: null, notes: '', isMuted: false, testInputMasterId: null, outputs: [] }
      : { notes: '', isMuted: false, testInputMasterId: null, outputs: [] };

    this.getOtherTestRows(kind).push(row);
  }

  removeOtherTestRow(kind: OtherTestKind, index: number): void {
    this.blockedTestsActionMessage = '';
    this.getOtherTestRows(kind).splice(index, 1);
  }

  private getCapabilityAllowed(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto,
    fallback: boolean
  ): boolean {
    const item = this.capabilities ? selector(this.capabilities) : null;
    return item?.allowed ?? fallback;
  }

  private getCapabilityReason(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto
  ): string | null {
    const item = this.capabilities ? selector(this.capabilities) : null;
    if (!item || item.allowed || !item.reason) {
      return null;
    }

    return item.reason;
  }

  canSaveTests(): boolean {
    const fallback = !this.isParentProjectCompleted && (this.statusId === 1 || this.statusId === 6);
    return this.getCapabilityAllowed(c => c.saveTests, fallback);
  }

  getSaveTestsBlockedReason(): string {
    if (this.canSaveTests()) {
      return '';
    }

    const reason = this.getCapabilityReason(c => c.saveTests);
    if (reason) {
      return reason;
    }
    if (this.isParentProjectCompleted) {
      return 'Tests are read-only because the parent project is completed.';
    }

    return 'Tests can only be saved while the slurry is in Draft or Draft Again.';
  }

  private canAppendClearNotes(): boolean {
    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.statusId === 3;
    return this.getCapabilityAllowed(c => c.appendClearNotes, fallback);
  }

  private isPersistedTestInputMaster(id: number | null | undefined): id is number {
    return typeof id === 'number' && id > 0;
  }

  canShowAppendClearNotes(testInputMasterId: number | null | undefined): boolean {
    return this.isPersistedTestInputMaster(testInputMasterId) && this.canAppendClearNotes();
  }

  get isTestsEditable(): boolean {
    return this.canSaveTests();
  }

  canShowTransientTestRowRemove(_testInputMasterId: number | null | undefined): boolean {
    return this.isTestsEditable;
  }

  canShowRemoveSingleTest(testInputMasterId: number | null | undefined): boolean {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return false;
    }

    const fallback = !this.isParentProjectCompleted && this.slurryId > 0 && this.statusId === 3;
    return this.getCapabilityAllowed(c => c.removeSingleTest, fallback);
  }

  getTestPrimaryOutput(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): import('../../../../services/slurry.service').BatchTestBriefDto | null {
    if (!outputs?.length) {
      return null;
    }

    return outputs.reduce((latest, current) =>
      current.testOutputMasterId > latest.testOutputMasterId ? current : latest
    );
  }

  getTestOutputBlockedReason(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): string {
    return this.getTestPrimaryOutput(outputs) ? '' : 'No batch output is recorded for this test yet.';
  }

  canShowTestOutputCard(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): boolean {
    return this.getTestPrimaryOutput(outputs) != null;
  }

  batchTestStatusLabel(statusNum: number): string {
    const map: Record<number, string> = {
      1: 'InProgress',
      2: 'Approved',
      3: 'Rejected',
      4: 'Repeat',
      5: 'Failed',
      6: 'Completed',
      7: 'Cancelled'
    };

    return map[statusNum] ?? String(statusNum);
  }

  get footerBlockedTestsActionMessage(): string {
    return this.blockedTestsActionMessage || this.parentBlockedTestsActionMessage;
  }

  getTestAttachmentCount(testInputMasterId: number | null | undefined): number {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return 0;
    }

    return this.testAttachmentCounts[testInputMasterId] ?? 0;
  }

  getTestAttachmentsLabel(testInputMasterId: number | null | undefined): string {
    return `View Attachments (${this.getTestAttachmentCount(testInputMasterId)})`;
  }

  getTestAttachmentsBlockedReason(testInputMasterId: number | null | undefined): string {
    if (this.isPersistedTestInputMaster(testInputMasterId)) {
      return '';
    }

    return 'Save this test first to view its attachments.';
  }

  onBlockedActionClick(reason: string | null | undefined): void {
    this.blockedTestsActionMessage = reason ? reason : '';
  }

  openTestNotesDialog(row: TestNoteRow): void {
    if (!this.isPersistedTestInputMaster(row.testInputMasterId)) {
      return;
    }

    const dialogRef = this.dialog.open(AppendNoteDialogComponent, {
      width: '960px',
      disableClose: true,
      data: {
        mode: 'test',
        slurryId: this.slurryId,
        testInputMasterId: row.testInputMasterId,
        originalNote: row.notes ?? ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && typeof result.updatedNotes === 'string') {
        row.notes = result.updatedNotes;
      }
    });
  }

  reviewTestOutput(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    this.emitOutputAction('review', outputs);
  }

  recordTestOutput(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    this.emitOutputAction('record', outputs);
  }

  openTestBatch(outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    this.emitOutputAction('batch', outputs);
  }

  private emitOutputAction(action: SlurryOutputActionContext['action'], outputs: import('../../../../services/slurry.service').BatchTestBriefDto[] | null | undefined): void {
    if (outputs?.length) {
      this.outputActionRequested.emit({ action, outputs });
    }
  }

  removeSingleTest(testInputMasterId: number): void {
    this.singleTestRemoveRequested.emit(testInputMasterId);
  }

  openTestAttachments(testInputMasterId: number | null | undefined, title: string): void {
    if (!this.isPersistedTestInputMaster(testInputMasterId)) {
      return;
    }

    this.testAttachmentsRequested.emit({ testInputMasterId, title });
  }

  private clearSectionRows(kind: OtherTestKind): void {
    const rows = this.getOtherTestRows(kind);
    rows.splice(0, rows.length);
  }

  private syncSectionSelectionFromRows(): void {
    for (const section of this.otherTestSections) {
      this.sectionSelected[section.kind] = this.getOtherTestRows(section.kind).length > 0;
    }
  }
}
